import { supabase } from "@/integrations/supabase/client";

export interface NorthStarMetrics {
  wpau: number; // Weekly Prompt-Activated Users
  wpauGrowth: number; // Week-over-week growth percentage
  wpe: number; // Weekly Prompt Executions
  wpeGrowth: number; // Week-over-week growth percentage
  firstSessionActivation: number; // % of signups that interact with prompts
  timeToFirstPrompt: number; // Average minutes from signup to first prompt interaction
  day7Retention: number; // % of activated users still active after 7 days
  day30Retention: number; // % of activated users still active after 30 days
  wauMauRatio: number; // Weekly Active Users / Monthly Active Users
  promptlabSessions: number; // Average PromptLab sessions per user this week
  newPromptsPublished: number; // New prompts published this week
  promptUtilization: number; // % of prompts used at least once in last 30 days
}

export interface ActivationMetrics {
  totalSignups: number;
  activatedUsers: number; // Users who copied/saved at least 1 prompt
  activationRate: number; // % of signups that activated
  avgTimeToFirstPrompt: number; // Minutes
  firstWeekRetention: number;
  firstMonthRetention: number;
}

export interface RetentionCohort {
  signupWeek: string;
  totalUsers: number;
  day7Retained: number;
  day30Retained: number;
  day7Rate: number;
  day30Rate: number;
}

export class NorthStarMetricsService {
  
  /**
   * Calculate Weekly Prompt-Activated Users (WPAU)
   * Users who copy or save prompts in the last 7 days
   */
  async calculateWPAU(startDate: Date, endDate: Date): Promise<{ current: number; previous: number; growth: number }> {
    try {
      // Current period WPAU - users who copied or saved prompts
      const { data: currentCopies, error: currentCopiesError } = await supabase
        .from('prompt_copies')
        .select('user_id')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (currentCopiesError) throw currentCopiesError;

      const { data: currentSaves, error: currentSavesError } = await supabase
        .from('user_saved_prompts')
        .select('user_id')
        .gte('saved_at', startDate.toISOString())
        .lte('saved_at', endDate.toISOString());

      if (currentSavesError) throw currentSavesError;

      // Previous period for comparison
      const previousEndDate = new Date(startDate);
      const previousStartDate = new Date(startDate);
      previousStartDate.setDate(previousStartDate.getDate() - 7);

      const { data: previousCopies, error: previousCopiesError } = await supabase
        .from('prompt_copies')
        .select('user_id')
        .gte('created_at', previousStartDate.toISOString())
        .lte('created_at', previousEndDate.toISOString());

      if (previousCopiesError) throw previousCopiesError;

      const { data: previousSaves, error: previousSavesError } = await supabase
        .from('user_saved_prompts')
        .select('user_id')
        .gte('saved_at', previousStartDate.toISOString())
        .lte('saved_at', previousEndDate.toISOString());

      if (previousSavesError) throw previousSavesError;

      // Combine users who copied or saved prompts
      const currentActivatedUsers = new Set([
        ...(currentCopies?.map(c => c.user_id) || []),
        ...(currentSaves?.map(s => s.user_id) || [])
      ]);

      const previousActivatedUsers = new Set([
        ...(previousCopies?.map(c => c.user_id) || []),
        ...(previousSaves?.map(s => s.user_id) || [])
      ]);

      const current = currentActivatedUsers.size;
      const previous = previousActivatedUsers.size;
      const growth = previous > 0 ? ((current - previous) / previous) * 100 : 0;

      return { current, previous, growth };
    } catch (error) {
      console.error('Error calculating WPAU:', error);
      return { current: 0, previous: 0, growth: 0 };
    }
  }

  /**
   * Calculate Weekly Prompt Executions (WPE)
   */
  async calculateWPE(startDate: Date, endDate: Date): Promise<{ current: number; previous: number; growth: number }> {
    try {
      // Current period
      const { data: currentWPE, error: currentError } = await supabase
        .from('prompt_copies')
        .select('id')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (currentError) throw currentError;

      // Previous period
      const previousEndDate = new Date(startDate);
      const previousStartDate = new Date(startDate);
      previousStartDate.setDate(previousStartDate.getDate() - 7);

      const { data: previousWPE, error: previousError } = await supabase
        .from('prompt_copies')
        .select('id')
        .gte('created_at', previousStartDate.toISOString())
        .lte('created_at', previousEndDate.toISOString());

      if (previousError) throw previousError;

      const current = currentWPE?.length || 0;
      const previous = previousWPE?.length || 0;
      const growth = previous > 0 ? ((current - previous) / previous) * 100 : 0;

      return { current, previous, growth };
    } catch (error) {
      console.error('Error calculating WPE:', error);
      return { current: 0, previous: 0, growth: 0 };
    }
  }

  /**
   * Calculate first-session activation rate
   */
  async calculateFirstSessionActivation(): Promise<number> {
    try {
      // Get total signups in last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data: totalUsers } = await supabase.rpc('get_new_users_in_period', {
        start_date: thirtyDaysAgo.toISOString(),
        end_date: new Date().toISOString()
      });

      // Get activated users (those with activation events)
      const { data: activatedUsers, error } = await supabase
        .from('user_activation_events')
        .select('user_id')
        .gte('created_at', thirtyDaysAgo.toISOString())
        .in('event_type', ['first_prompt_copy', 'first_prompt_save']);

      if (error) throw error;

      const uniqueActivated = new Set(activatedUsers?.map(u => u.user_id) || []).size;
      const total = totalUsers || 0;

      return total > 0 ? (uniqueActivated / total) * 100 : 0;
    } catch (error) {
      console.error('Error calculating first session activation:', error);
      return 0;
    }
  }

  /**
   * Calculate average time to first prompt interaction
   */
  async calculateTimeToFirstPrompt(): Promise<number> {
    try {
      const { data: activationEvents, error } = await supabase
        .from('user_activation_events')
        .select('user_id, created_at')
        .in('event_type', ['first_prompt_copy', 'first_prompt_save'])
        .order('created_at', { ascending: false })
        .limit(100); // Sample recent activations

      if (error) throw error;

      if (!activationEvents || activationEvents.length === 0) return 0;

      // Get user creation dates
      const userIds = activationEvents.map(e => e.user_id);
      const { data: userCreationDates, error: userError } = await supabase.rpc('get_user_creation_dates');

      if (userError) throw userError;

      const userCreationMap = new Map(
        userCreationDates?.map(u => [u.created_at, new Date(u.created_at)]) || []
      );

      let totalMinutes = 0;
      let validSamples = 0;

      activationEvents.forEach(event => {
        const activationTime = new Date(event.created_at);
        // Find closest user creation time (this is simplified - in production you'd want exact user mapping)
        const creationTimes = Array.from(userCreationMap.values());
        const closestCreation = creationTimes.reduce((closest, current) => {
          return Math.abs(current.getTime() - activationTime.getTime()) < 
                 Math.abs(closest.getTime() - activationTime.getTime()) ? current : closest;
        });

        if (closestCreation && activationTime > closestCreation) {
          const diffMinutes = (activationTime.getTime() - closestCreation.getTime()) / (1000 * 60);
          if (diffMinutes < 10080) { // Less than 7 days
            totalMinutes += diffMinutes;
            validSamples++;
          }
        }
      });

      return validSamples > 0 ? totalMinutes / validSamples : 0;
    } catch (error) {
      console.error('Error calculating time to first prompt:', error);
      return 0;
    }
  }

  /**
   * Calculate retention rates
   */
  async calculateRetentionRates(): Promise<{ day7: number; day30: number }> {
    try {
      const now = new Date();
      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const thirtyDaysAgo = new Date(now);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      // Get users who activated 7+ days ago
      const { data: activatedUsers7Days, error: error7 } = await supabase
        .from('user_activation_events')
        .select('user_id, created_at')
        .lte('created_at', sevenDaysAgo.toISOString())
        .in('event_type', ['first_prompt_copy', 'first_prompt_save']);

      if (error7) throw error7;

      // Get users who activated 30+ days ago
      const { data: activatedUsers30Days, error: error30 } = await supabase
        .from('user_activation_events')
        .select('user_id, created_at')
        .lte('created_at', thirtyDaysAgo.toISOString())
        .in('event_type', ['first_prompt_copy', 'first_prompt_save']);

      if (error30) throw error30;

      // Check who's still active (had prompt interactions in last 7 days)
      const { data: recentActivity, error: activityError } = await supabase
        .from('prompt_copies')
        .select('user_id')
        .gte('created_at', sevenDaysAgo.toISOString());

      if (activityError) throw activityError;

      const activeUserIds = new Set(recentActivity?.map(a => a.user_id) || []);
      
      const activatedUserIds7Days = new Set(activatedUsers7Days?.map(u => u.user_id) || []);
      const activatedUserIds30Days = new Set(activatedUsers30Days?.map(u => u.user_id) || []);

      const retained7Days = Array.from(activatedUserIds7Days).filter(id => activeUserIds.has(id)).length;
      const retained30Days = Array.from(activatedUserIds30Days).filter(id => activeUserIds.has(id)).length;

      const day7Retention = activatedUserIds7Days.size > 0 ? (retained7Days / activatedUserIds7Days.size) * 100 : 0;
      const day30Retention = activatedUserIds30Days.size > 0 ? (retained30Days / activatedUserIds30Days.size) * 100 : 0;

      return { day7: day7Retention, day30: day30Retention };
    } catch (error) {
      console.error('Error calculating retention rates:', error);
      return { day7: 0, day30: 0 };
    }
  }

  /**
   * Calculate WAU/MAU ratio
   */
  async calculateWAUMAURatio(): Promise<number> {
    try {
      const now = new Date();
      const oneWeekAgo = new Date(now);
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      const oneMonthAgo = new Date(now);
      oneMonthAgo.setDate(oneMonthAgo.getDate() - 30);

      // Weekly Active Users
      const { data: wauData, error: wauError } = await supabase
        .from('prompt_copies')
        .select('user_id')
        .gte('created_at', oneWeekAgo.toISOString());

      if (wauError) throw wauError;

      // Monthly Active Users
      const { data: mauData, error: mauError } = await supabase
        .from('prompt_copies')
        .select('user_id')
        .gte('created_at', oneMonthAgo.toISOString());

      if (mauError) throw mauError;

      const wau = new Set(wauData?.map(u => u.user_id) || []).size;
      const mau = new Set(mauData?.map(u => u.user_id) || []).size;

      return mau > 0 ? (wau / mau) * 100 : 0;
    } catch (error) {
      console.error('Error calculating WAU/MAU ratio:', error);
      return 0;
    }
  }

  /**
   * Get comprehensive North Star metrics
   */
  async getNorthStarMetrics(): Promise<NorthStarMetrics> {
    try {
      const now = new Date();
      const oneWeekAgo = new Date(now);
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

      const [
        wpauData,
        wpeData,
        firstSessionActivation,
        timeToFirstPrompt,
        retentionRates,
        wauMauRatio
      ] = await Promise.all([
        this.calculateWPAU(oneWeekAgo, now),
        this.calculateWPE(oneWeekAgo, now),
        this.calculateFirstSessionActivation(),
        this.calculateTimeToFirstPrompt(),
        this.calculateRetentionRates(),
        this.calculateWAUMAURatio()
      ]);

      // Get PromptLab sessions data
      const { data: sessionsData } = await supabase
        .from('promptlab_sessions')
        .select('user_id')
        .gte('created_at', oneWeekAgo.toISOString());

      const promptlabSessions = sessionsData ? new Set(sessionsData.map(s => s.user_id)).size : 0;

      // Get new prompts published this week
      const { data: newPrompts } = await supabase
        .from('prompts')
        .select('id')
        .gte('created_at', oneWeekAgo.toISOString())
        .eq('status', 'approved');

      const newPromptsPublished = newPrompts?.length || 0;

      // Calculate prompt utilization (simplified version)
      const thirtyDaysAgo = new Date(now);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data: totalPrompts } = await supabase
        .from('prompts')
        .select('id')
        .eq('status', 'approved');

      const { data: usedPrompts } = await supabase
        .from('prompt_copies')
        .select('prompt_id')
        .gte('created_at', thirtyDaysAgo.toISOString());

      const uniqueUsedPrompts = new Set(usedPrompts?.map(p => p.prompt_id) || []).size;
      const totalPromptsCount = totalPrompts?.length || 1;
      const promptUtilization = (uniqueUsedPrompts / totalPromptsCount) * 100;

      return {
        wpau: wpauData.current,
        wpauGrowth: wpauData.growth,
        wpe: wpeData.current,
        wpeGrowth: wpeData.growth,
        firstSessionActivation,
        timeToFirstPrompt,
        day7Retention: retentionRates.day7,
        day30Retention: retentionRates.day30,
        wauMauRatio,
        promptlabSessions,
        newPromptsPublished,
        promptUtilization
      };
    } catch (error) {
      console.error('Error getting North Star metrics:', error);
      // Return default values on error
      return {
        wpau: 0,
        wpauGrowth: 0,
        wpe: 0,
        wpeGrowth: 0,
        firstSessionActivation: 0,
        timeToFirstPrompt: 0,
        day7Retention: 0,
        day30Retention: 0,
        wauMauRatio: 0,
        promptlabSessions: 0,
        newPromptsPublished: 0,
        promptUtilization: 0
      };
    }
  }
}

export const northStarMetricsService = new NorthStarMetricsService();
